/**
 * useTenantInboxIds - Hook para manter cache dos inbox_ids do tenant ativo
 * 
 * Versão: 1.0.0 - 2026-02-13
 * 
 * Utilizado para filtrar eventos Realtime por tenant nos callbacks,
 * evitando processamento desnecessário de eventos de outros tenants.
 * 
 * A tabela conversations não possui tenant_id direto, então usamos
 * inbox_id como proxy para identificar o tenant.
 */

import { useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('TenantInboxIds');

/**
 * Retorna um Set<string> com os inbox_ids do tenant ativo.
 * O Set é mantido em um ref para uso seguro em callbacks de Realtime.
 * 
 * @returns inboxIdsRef - Ref com Set de inbox IDs do tenant
 */
export function useTenantInboxIds() {
  const { currentTenant } = useAuth();
  const inboxIdsRef = useRef<Set<string>>(new Set());

  // Query simples para buscar os IDs das inboxes do tenant
  const { data: inboxIds } = useQuery({
    queryKey: ['tenant-inbox-ids-for-realtime', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return [];

      // v1.1.0: Migrado para RPC server-side (get_tenant_inboxes_safe)
      // Elimina SELECT direto na tabela inboxes ao abrir a tela do Chat.
      // Reutiliza a mesma RPC já consumida por useInboxes, com camada v2.0
      // de validação de tenant (get_validated_tenant_id) e log de bypass.
      const { data, error } = await supabase.rpc('get_tenant_inboxes_safe', {
        p_tenant_hint: currentTenant.id,
      });

      if (error) {
        logger.error('Erro ao buscar inbox IDs do tenant via RPC:', error);
        return [];
      }

      // Extrai apenas os IDs — payload completo é descartado pois o hook
      // só precisa do Set para filtrar eventos Realtime por tenant.
      const ids = (data || []).map((i: any) => i.id);
      logger.debug(`Carregados ${ids.length} inbox IDs para filtro Realtime`);
      return ids;
    },
    enabled: !!currentTenant?.id,
    staleTime: 5 * 60 * 1000, // 5 minutos - inboxes mudam raramente
    gcTime: 10 * 60 * 1000,
  });

  // Atualiza ref sempre que os dados mudam
  useEffect(() => {
    inboxIdsRef.current = new Set(inboxIds || []);
  }, [inboxIds]);

  return inboxIdsRef;
}

/**
 * Verifica se um inbox_id pertence ao tenant ativo
 * Função helper para uso nos callbacks de Realtime
 */
export function isFromCurrentTenant(
  inboxIdsRef: React.RefObject<Set<string>>,
  payload: any
): boolean {
  const inboxId = payload?.new?.inbox_id || payload?.old?.inbox_id;
  if (!inboxId) return true; // Se não tem inbox_id, processa por segurança
  return inboxIdsRef.current?.has(inboxId) ?? true;
}
